import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Tick01Icon,
  FlashIcon,
  Link01Icon,
  UserGroupIcon,
  CustomerService01Icon,
  ArrowRight01Icon,
  Mail01Icon,
  Search01Icon,
  DashboardSquare01Icon,
  Menu01Icon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import ResponsiveModal from '../components/ResponsiveModal';
import { api } from '../utils/api';
import { FLAGS } from '../config/flags';
import ReelText from '../components/ui/ReelText';

const LinkCard = ({ agent, viewMode }) => {
  const navigate = useNavigate();
  const isActive = agent.status === 'active';
  const isWithdrawal = agent.transaction_type === 'withdrawal';

  const getButtonText = () => {
    if (!agent.rate) return 'Contact for Link';
    return agent.transaction_type === 'deposit' ? 'Buy USD' : 'Sell USD';
  };

  if (viewMode === 'list') {
    return (
      <div className={`bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-4 rounded-2xl border transition-all flex items-center gap-4 ${isActive ? 'border-light-gray/20 dark:border-dark-border/20 hover:border-soft-green' : 'border-red-500/20 opacity-75 grayscale-[0.5]'}`}>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-black uppercase shrink-0 relative ${isActive ? 'bg-soft-green/10 text-soft-green border border-soft-green/20' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 border-gray-200'}`}>
          {(agent.name || 'A')[0]}
          {isActive && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-soft-green rounded-full border-2 border-white dark:border-dark-surface animate-pulse" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold text-muted-gray uppercase tracking-widest truncate">By {agent.name}</p>
          <div className="flex items-center gap-2">
            <p className="font-black text-charcoal dark:text-white truncate">₦{parseFloat(agent.rate || 0).toLocaleString()}</p>
            {!isActive && <span className="text-[8px] font-black text-red-500 uppercase tracking-tighter bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded">Inactive</span>}
          </div>
        </div>
        <div className={`hidden sm:block px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-wider ${
          agent.transaction_type === 'deposit' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
        }`}>
          {agent.transaction_type}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => agent.link_token ? navigate(`/order/pay/${agent.link_token}`) : navigate('/support')}
            disabled={!isActive}
            className={`py-2.5 px-4 rounded-xl text-[10px] font-black whitespace-nowrap ${!isActive ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 pointer-events-none' : isWithdrawal ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
          >
            {isActive ? getButtonText() : 'Closed'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-6 rounded-[2.5rem] border shadow-float hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between h-full relative overflow-hidden ${isActive ? 'border-light-gray/20 dark:border-dark-border/20' : 'border-red-500/10 opacity-80'}`}>
      {!isActive && (
        <div className="absolute inset-0 bg-white/40 dark:bg-black/20 backdrop-blur-[1px] pointer-events-none z-10" />
      )}

      <div>
        <div className="flex justify-between items-center gap-4 mb-6 relative z-20">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-sm font-black uppercase shrink-0 transition-transform group-hover:scale-110 ${isActive ? 'bg-soft-green/10 text-soft-green shadow-sm' : 'bg-gray-100 text-gray-400'}`}>
              {(agent.name || 'A')[0]}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-muted-gray uppercase tracking-widest truncate whitespace-nowrap">By <ReelText text={agent.name} /></p>
              {isActive ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div className="w-1.5 h-1.5 bg-soft-green rounded-full animate-pulse" />
                  <span className="text-[8px] font-black uppercase text-soft-green tracking-widest">Active Link</span>
                </div>
              ) : (
                <span className="text-[8px] font-black uppercase text-red-500 tracking-widest">Link Inactive</span>
              )}
            </div>
          </div>
          <div className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
            agent.transaction_type === 'deposit' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
          }`}>
            {agent.transaction_type}
          </div>
        </div>

        <div className="space-y-1 mb-8 relative z-20">
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-black text-charcoal dark:text-white tracking-tighter"><ReelText text={`₦${parseFloat(agent.rate || 0).toLocaleString()}`} /></span>
            <span className="text-xs font-bold text-muted-gray">/$</span>
          </div>
          <div className="flex items-center gap-2 text-[10px] font-medium text-muted-gray">
            <HugeiconsIcon icon={FlashIcon} size={12} className="text-yellow-500" />
            Limits: ${parseFloat(agent.min || 0).toLocaleString()} - ${parseFloat(agent.max || 0).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="relative z-20">
        <Button
          onClick={() => agent.link_token ? navigate(`/order/pay/${agent.link_token}`) : navigate('/support')}
          disabled={!isActive}
          className={`w-full py-4 rounded-2xl font-black shadow-lg transition-all ${isActive ? 'group-hover:shadow-soft-green/20' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 grayscale cursor-not-allowed'} ${isWithdrawal && isActive ? 'bg-red-500 hover:bg-red-600 text-white shadow-red-500/20' : ''}`}
        >
          {isActive ? getButtonText() : 'Currently Inactive'}
        </Button>
      </div>
    </div>
  );
};

const HOW_IT_WORKS_STEPS = [
  { title: "Get Private Link", description: "Contact your agent directly to receive your unique link.", icon: Link01Icon, color: "bg-blue-500/10 text-blue-500" },
  { title: "Securely Pay", description: "Follow agent instructions exactly. Amount must match.", icon: Tick01Icon, color: "bg-green-500/10 text-green-500" },
  { title: "Instant Release", description: "Upload receipt. Agent releases funds once verified.", icon: FlashIcon, color: "bg-yellow-500/10 text-yellow-500" },
  { title: "Permanent Binding", description: "The agent will be permanently linked to your account.", icon: UserGroupIcon, color: "bg-purple-500/10 text-purple-500" }
];

const Marketplace = () => {
  const [recentAgents, setRecentAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [howStep, setHowStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'deposit' | 'withdrawal'
  const [viewMode, setViewMode] = useState('grid');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await api.get('/client/recent-agents');
        const agents = response.data?.data?.agents || response.data?.agents || [];
        const mapped = agents.map(agent => ({
          uid: agent.uid,
          name: agent.name,
          email: agent.email,
          verified: agent.is_verified,
          rate: agent.latest_active_order?.current_rate,
          min: agent.latest_active_order?.min_amount_usd,
          max: agent.latest_active_order?.total_amount_usd,
          link_token: agent.latest_active_order?.link_token,
          transaction_type: agent.latest_active_order?.transaction_type,
          status: agent.latest_active_order?.status || 'active',
        }));
        setRecentAgents(mapped);
      } catch (err) { console.warn('Fetch failed', err); } 
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const filteredLinks = useMemo(() => {
    return recentAgents.filter(a => {
      const matchesSearch = a.name?.toLowerCase().includes(searchTerm.toLowerCase()) || a.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = typeFilter === 'all' || a.transaction_type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [recentAgents, searchTerm, typeFilter]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-soft-green"></div>
      </div>
    );
  }

  if (recentAgents.length === 0 || FLAGS.FORCE_CLIENT_SPLASH) {
    return (
      <>
        <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-4 max-w-4xl mx-auto py-20">
          <div className="bg-soft-green/10 p-6 rounded-full inline-block mb-8 animate-float">
            <HugeiconsIcon icon={CustomerService01Icon} size={64} className="text-soft-green" />
          </div>
          <h1 className="text-5xl sm:text-6xl font-black text-charcoal dark:text-white mb-6 tracking-tight leading-tight">Private Access <span className="text-soft-green italic">Only</span></h1>
          <p className="text-xl text-muted-gray dark:text-gray-300 mb-12 leading-relaxed max-w-2xl">Safzi is a secure, direct-to-agent model. Please contact your preferred agent to receive your unique <span className="text-soft-green font-bold">Private Access Link</span>.</p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center w-full max-w-lg">
            <Button onClick={() => { setHowStep(0); setIsHowItWorksOpen(true); }} className="flex-1 py-5 rounded-[2rem] text-xl font-black shadow-float-lg transform transition-transform hover:scale-105 active:scale-95">How it works</Button>
            <Button onClick={() => setIsSupportOpen(true)} variant="secondary" className="flex-1 py-5 rounded-[2rem] font-black text-xl shadow-md transform transition-transform hover:scale-105 active:scale-95">Contact Support</Button>
          </div>
        </div>
        
        <ResponsiveModal isOpen={isHowItWorksOpen} onClose={() => { setIsHowItWorksOpen(false); setHowStep(0); }} title={`How it Works (${howStep + 1}/4)`}>
          <div className="py-4 space-y-8">
            <div className="flex gap-2 px-1">{[0, 1, 2, 3].map((s) => (<div key={s} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${howStep >= s ? 'bg-soft-green shadow-[0_0_10px_rgba(74,222,128,0.5)]' : 'bg-gray-200 dark:bg-gray-700'}`}></div>))}</div>
            <div className="text-center space-y-6 animate-in fade-in zoom-in-95 duration-300 min-h-[280px] flex flex-col justify-center">
              <div className={`w-24 h-24 ${HOW_IT_WORKS_STEPS[howStep].color} rounded-[2rem] flex items-center justify-center mx-auto shadow-sm transform transition-transform hover:rotate-6`}><HugeiconsIcon icon={HOW_IT_WORKS_STEPS[howStep].icon} size={48} /></div>
              <div><h3 className="text-2xl font-black text-charcoal dark:text-white mb-2 tracking-tight">{HOW_IT_WORKS_STEPS[howStep].title}</h3><p className="text-muted-gray font-medium leading-relaxed max-w-xs mx-auto text-lg">{HOW_IT_WORKS_STEPS[howStep].description}</p></div>
            </div>
            <div className="flex gap-4">
              <Button variant="secondary" onClick={() => howStep > 0 ? setHowStep(howStep - 1) : setIsHowItWorksOpen(false)} className="flex-1 py-5 rounded-3xl font-black text-lg">{howStep > 0 ? 'Back' : 'Close'}</Button>
              <Button onClick={() => howStep < 3 ? setHowStep(howStep + 1) : setIsHowItWorksOpen(false)} className="flex-[2] py-5 rounded-3xl font-black text-lg gap-2 shadow-float-lg">{howStep < 3 ? 'Next Step' : 'Launch App'} {howStep < 3 && <HugeiconsIcon icon={ArrowRight01Icon} size={20} />}</Button>
            </div>
          </div>
        </ResponsiveModal>

        <ResponsiveModal isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} title="Contact Support">
          <div className="py-6 text-center space-y-6">
            <div className="bg-soft-green/10 p-6 rounded-full inline-block text-soft-green mb-2"><HugeiconsIcon icon={CustomerService01Icon} size={48} /></div>
            <p className="text-muted-gray px-4 font-medium leading-relaxed">Need help getting a link? Our team is available 24/7.</p>
            <div className="grid grid-cols-1 gap-3 px-4">
              <Button onClick={() => navigate('/support')} className="w-full py-4 rounded-2xl font-black gap-2"><HugeiconsIcon icon={Mail01Icon} size={20} />Open Support Center</Button>
              <Button variant="secondary" onClick={() => setIsSupportOpen(false)} className="w-full py-4 rounded-2xl font-bold">Close</Button>
            </div>
          </div>
        </ResponsiveModal>
      </>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-24 sm:pb-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-1">
          <h1 className="text-4xl font-black text-charcoal dark:text-white tracking-tight"><ReelText text="Recent Links" /></h1>
          <p className="text-muted-gray font-medium">Quick access to your trusted agent payment links.</p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <HugeiconsIcon icon={Search01Icon} size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-gray" />
            <input 
              type="text" 
              placeholder="Search agents..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white dark:bg-dark-surface border border-light-gray/20 dark:border-dark-border/20 rounded-2xl text-sm focus:ring-2 focus:ring-soft-green outline-none transition-all shadow-sm"
            />
          </div>
          <div className="flex bg-white dark:bg-dark-surface p-1.5 rounded-2xl border border-light-gray/20 dark:border-dark-border/20 shadow-sm">
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-soft-green text-white shadow-md' : 'text-muted-gray hover:text-charcoal dark:hover:text-white'}`}><HugeiconsIcon icon={DashboardSquare01Icon} size={18} /></button>
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-xl transition-all ${viewMode === 'list' ? 'bg-soft-green text-white shadow-md' : 'text-muted-gray hover:text-charcoal dark:hover:text-white'}`}><HugeiconsIcon icon={Menu01Icon} size={18} /></button>
          </div>
        </div>
      </div>

      {/* FILTER TABS */}
      <div className="flex gap-2 bg-white/50 dark:bg-dark-surface/50 p-1 rounded-2xl w-full sm:w-fit border border-light-gray/10 shadow-sm">
        {['all', 'deposit', 'withdrawal'].map(type => (
          <button
            key={type}
            onClick={() => setTypeFilter(type)}
            className={`flex-1 sm:flex-none px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${typeFilter === type ? 'bg-white dark:bg-dark-surface text-charcoal dark:text-white shadow-sm' : 'text-muted-gray hover:text-charcoal'}`}
          >
            {type === 'deposit' ? 'Buy' : type === 'withdrawal' ? 'Sell' : 'All'}
          </button>
        ))}
      </div>

      {filteredLinks.length === 0 ? (
        <div className="p-20 text-center bg-white/50 dark:bg-dark-surface/50 rounded-[3rem] border border-dashed border-light-gray dark:border-dark-border">
          <p className="text-muted-gray font-black uppercase tracking-widest text-[10px]">No matching links found</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-3"}>
          {filteredLinks.map((agent, index) => (
            <div key={agent.link_token || `${agent.uid}-${index}`} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <LinkCard agent={agent} viewMode={viewMode} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Marketplace;
